import axios from 'axios';

const fallbackBase =
  'https://p684534.mittwaldserver.info/sander/backend/ms-onlineshop/public';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || fallbackBase,
  timeout: 15000,
});

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 350;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Client-side Rate Limiter (reduziert 429-Risiko im Frontend)
const WINDOW_MS = 900;
const MAX_REQ_PER_WINDOW = 5;        // max. 5 Requests pro ~0.9s
const MIN_GAP_MS = 150;              // Mindestabstand zwischen zwei Requests
const BURST_COOLDOWN_MS = 900;      // Extra-Pause nach ausgeschöpftem Window
const COOLDOWN_AFTER_429_MS = 4200;  // zusätzliche Ruhezeit nach 429/503
const PER_REQUEST_BASE_DELAY_MS = 10;
const PER_REQUEST_JITTER_MS = 60;
let lastRequestAt = 0;
let requestTimestamps = [];
let gate = Promise.resolve();
let cooldownUntil = 0;               // globaler Cooldown, wird nach 429 gesetzt

const applyRateLimit = () => {
  gate = gate.then(async () => {
    const now = Date.now();

    // Aktiven Cooldown beachten (z. B. nach 429)
    if (now < cooldownUntil) {
      await sleep(cooldownUntil - now);
    }

    // Mindestabstand erzwingen
    const gap = now - lastRequestAt;
    if (gap < MIN_GAP_MS) {
      await sleep(MIN_GAP_MS - gap);
    }

    // Sliding-Window prüfen
    const ts = Date.now();
    requestTimestamps = requestTimestamps.filter((t) => ts - t <= WINDOW_MS);
    if (requestTimestamps.length >= MAX_REQ_PER_WINDOW) {
      const wait = WINDOW_MS - (ts - requestTimestamps[0]) + BURST_COOLDOWN_MS;
      cooldownUntil = Math.max(cooldownUntil, Date.now() + BURST_COOLDOWN_MS);
      await sleep(wait);
    }

    // Zusätzliche Grundverzögerung + Jitter gegen synchrone Bursts (überspringen, wenn lange Pause war)
    const gapSinceLast = now - lastRequestAt;
    const skipMicroDelay = requestTimestamps.length === 0 && gapSinceLast > WINDOW_MS;
    if (!skipMicroDelay) {
      const jitterMs = Math.random() * PER_REQUEST_JITTER_MS;
      await sleep(PER_REQUEST_BASE_DELAY_MS + jitterMs);
    }

    requestTimestamps.push(Date.now());
    lastRequestAt = Date.now();
  });

  return gate;
};

api.interceptors.request.use(
  async (config) => {
    await applyRateLimit();
    return config;
  },
  (error) => Promise.reject(error)
);

// Retry + Backoff auf 429/503
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    // Kein Retry bei manuellen Abbrüchen
    if (!config || axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    if (status === 429 || status === 503) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount >= MAX_RETRIES) {
        return Promise.reject(error);
      }

      config.__retryCount += 1;

      const retryAfterHeader = error.response?.headers?.['retry-after'];
      let delayMs = BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1);
      const retryAfterSeconds = Number(retryAfterHeader);
      if (!Number.isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
        delayMs = retryAfterSeconds * 1000;
      }

      const penalty = !Number.isNaN(retryAfterSeconds) && retryAfterSeconds > 0
        ? retryAfterSeconds * 1000
        : COOLDOWN_AFTER_429_MS * config.__retryCount;

      // Nach 429/503 den globalen Cooldown verlängern, damit Folge-Requests warten
      cooldownUntil = Math.max(cooldownUntil, Date.now() + penalty);

      await sleep(delayMs);
      return api(config);
    }

    return Promise.reject(error);
  }
);

// Utility-Exports für bestehenden Code (z. B. isCancel)
api.isCancel = axios.isCancel;

export default api;
